import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks";
import { FullScreenLoader } from "../../../ui";
import { ImpersonationBanner } from "../../../components/layout/ImpersonationBanner";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Protected route component that uses the useAuth hook to check authentication.
 * Redirects to /auth if user is not authenticated.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // The impersonation banner is mounted here — the universal authenticated
  // wrapper — so it shows on every protected page regardless of whether that
  // page uses PageLayout (e.g. EvaluatorsPage does not). It sits in flow above
  // the page content (pushing it down, not overlaying), and renders nothing
  // unless a staff "view as customer" session is active.
  return (
    <div className="flex h-full flex-col">
      <ImpersonationBanner />
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
};
